import type { AssetContainer } from '@babylonjs/core/assetContainer';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import '@babylonjs/loaders/glTF';

import type { Kb3dManager } from '@kb3d/models/classes/kb3d-manager';
import { KbGeometry } from '@kb3d/models/classes/primitives/kb-geometry';
import { MeshService } from '@kb3d/models/services/mesh-service';
import { Token } from '@kb3d/models/classes/token';

import type { KbViewer } from '../kb-viewer';
import { CustomMeshNode } from '@kb3d/models/classes/meshes/custom-mesh-node';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { KbQuaternion } from '@kb3d/models/classes/primitives/kb-quaternion';
import { MaterialNode } from '@kb3d/models/classes/materials/material-node';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { KbColor } from '@kb3d/models/classes/primitives/kb-color';
import { Material } from '@babylonjs/core/Materials/material';
import { textureChannelMap } from '@kb3d/view/render/textures/texture';
import { Texture } from '@babylonjs/core/Materials/Textures';
import { eTextureChannel } from '@kb3d/models/classes/enums';
import { FileTextureLayer } from '@kb3d/models/classes';

const babylonTextureProperties = Object.entries(textureChannelMap);

const imageFormatMagicBytes = [
    { magicBytes: [0x89, 0x50, 0x4e, 0x47], extension: 'png' },
    { magicBytes: [0xff, 0xd8], extension: 'jpg' },
    { magicBytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], extension: 'gif' },
    { magicBytes: [0x42, 0x4d], extension: 'bmp' },
];

function getImageFormat(data: Uint8Array): string | null {
    for (const { magicBytes, extension } of imageFormatMagicBytes) {
        let match = true;
        for (let i = 0; i < magicBytes.length; i++) {
            if (data[i] !== magicBytes[i]) {
                match = false;
                break;
            }
        }
        if (match) {
            return extension;
        }
    }
    return null;
}

type GltfUvDocument = {
    extensionsUsed?: string[];
    accessors?: Array<{ componentType?: number; normalized?: boolean; type?: string }>;
    meshes?: Array<{ primitives?: Array<{ attributes?: { TEXCOORD_0?: number } }> }>;
};

function hasNormalizedDracoUvs(data: ArrayBuffer): boolean {
    const view = new DataView(data);
    if (
        data.byteLength < 20 ||
        view.getUint32(0, true) !== 0x46546c67 ||
        view.getUint32(16, true) !== 0x4e4f534a ||
        view.getUint32(12, true) > data.byteLength - 20
    ) {
        return false;
    }

    const jsonLength = view.getUint32(12, true);
    const gltf = JSON.parse(new TextDecoder().decode(new Uint8Array(data, 20, jsonLength))) as GltfUvDocument;
    if (!gltf.extensionsUsed?.includes('KHR_draco_mesh_compression')) {
        return false;
    }
    return (
        gltf.meshes?.some(mesh =>
            mesh.primitives?.some(primitive => {
                const index = primitive.attributes?.TEXCOORD_0;
                const accessor = index === undefined ? undefined : gltf.accessors?.[index];
                return accessor?.componentType === 5123 && accessor.normalized === true && accessor.type === 'VEC2';
            })
        ) === true
    );
}
export class GLTFKb3dImporter {
    constructor(private viewer: KbViewer) {}

    async importGlb(manager: Kb3dManager, files: File[]) {
        const viewer = this.viewer;
        const containers: Array<{ container: AssetContainer; normalizedDracoUvs: boolean }> = [];
        for (const file of files) {
            containers.push(await loadGlb(file));
        }

        const meshNodes: CustomMeshNode[] = [];
        const materialNodes: MaterialNode[] = [];
        const textureNodes: FileTextureLayer[] = [];
        const imageDataByUrl = new Map<string, Blob>();

        for (const { container, normalizedDracoUvs } of containers) {
            const materialMapping = new Map<Material, MaterialNode>();
            for (const material of container.materials) {
                const materialNode = manager.create(Token.AdvancedMaterialNode);
                materialNode.name = material.name;
                materialNode.alpha = material.alpha;
                materialNode.doubleSided = !material.backFaceCulling;

                if (material instanceof PBRMaterial) {
                    materialNode.albedoColor = KbColor.From(material.albedoColor.toGammaSpace());
                    materialNode.emissiveColor = KbColor.From(material.emissiveColor);
                    materialNode.ambientColor = KbColor.From(material.ambientColor.toGammaSpace());
                    materialNode.reflectionColor = KbColor.From(material.reflectionColor.toGammaSpace());
                    materialNode.metallic = material.metallic || 0;
                    materialNode.roughness = material.roughness || 0;
                    if (
                        material.metallicTexture &&
                        material.useMetallnessFromMetallicTextureBlue &&
                        material.useRoughnessFromMetallicTextureGreen &&
                        !material.useRoughnessFromMetallicTextureAlpha
                    ) {
                        materialNode.metallicRoughnessTextureUsesGltfChannels = true;
                    }

                    if (material.bumpTexture && material.invertNormalMapY && material.forceIrradianceInFragment) {
                        materialNode.normalTextureUsesGltfConventions = true;
                    }

                    // extract the textures
                    for (const [kb3dProp, bjsProp] of babylonTextureProperties) {
                        if (typeof bjsProp !== 'string') {
                            continue;
                        }
                        if (!(bjsProp in material && material[bjsProp])) {
                            continue;
                        }
                        const texture = material[bjsProp] as Texture;
                        const textureNode = manager.create(Token.FileTextureLayer);
                        if (!texture._buffer) {
                            continue;
                        }
                        let dataUrl = '';
                        let imgBlob: Blob;
                        if (ArrayBuffer.isView(texture._buffer)) {
                            const bytes = new Uint8Array(
                                texture._buffer.buffer, texture._buffer.byteOffset, texture._buffer.byteLength
                            );
                            const format = getImageFormat(bytes) || 'unknown';
                            imgBlob = new Blob([bytes.slice()], { type: 'image/' + format });
                        } else if (texture._buffer instanceof Blob) {
                            imgBlob = texture._buffer;
                        } else {
                            console.warn('unknown texture buffer type', texture._buffer);
                            continue;
                        }
                        dataUrl = URL.createObjectURL(imgBlob);
                        imageDataByUrl.set(dataUrl, imgBlob);

                        textureNode.channel = kb3dProp as eTextureChannel;
                        textureNode.name = texture.name || `${material.name}_${kb3dProp}`;
                        textureNode.uOffset = texture.uOffset;
                        textureNode.vOffset = texture.vOffset;
                        textureNode.uScale = texture.uScale;
                        // The texture renderer negates file texture V scale; cancel that for glTF UVs.
                        textureNode.vScale = -texture.vScale;
                        textureNode.uAngle = (texture.uAng * 180) / Math.PI;
                        textureNode.vAngle = (texture.vAng * 180) / Math.PI;
                        textureNode.wAngle = (texture.wAng * 180) / Math.PI;
                        textureNode.uPivot = texture.uRotationCenter;
                        textureNode.vPivot = texture.vRotationCenter;
                        textureNode.wPivot = texture.wRotationCenter;
                        // This will be a blob url
                        textureNode.path = dataUrl;

                        materialNode.textures.push(textureNode);
                        textureNodes.push(textureNode);
                    }
                } else if (material instanceof StandardMaterial) {
                    materialNode.albedoColor = KbColor.From(material.diffuseColor.toGammaSpace());
                    materialNode.emissiveColor = KbColor.From(material.emissiveColor);
                    materialNode.ambientColor = KbColor.From(material.ambientColor.toGammaSpace());
                } else {
                    console.warn('Unsupported material type:', material);
                }
                materialNodes.push(materialNode);
                materialMapping.set(material, materialNode);
            }

            for (const aMesh of container.meshes) {
                if (!(aMesh instanceof Mesh)) {
                    continue;
                }
                const mesh = aMesh;
                if (!mesh.geometry) {
                    continue;
                }

                const newGeometry = new KbGeometry();
                newGeometry.indices = new Uint32Array(mesh.geometry.getIndices() || []);
                newGeometry.positions = new Float32Array(
                    mesh.geometry.getVerticesData(VertexBuffer.PositionKind) || []
                );
                if (newGeometry.indices.length === 0 || newGeometry.positions.length === 0) {
                    return null;
                }

                const normals = mesh.geometry.getVerticesData(VertexBuffer.NormalKind);
                if (normals) {
                    newGeometry.normals = new Float32Array(normals);
                }
                const tangents = mesh.geometry.getVerticesData(VertexBuffer.TangentKind);
                if (tangents) {
                    newGeometry.tangents = new Float32Array(tangents);
                }
                const uvs = mesh.geometry.getVerticesData(VertexBuffer.UVKind);
                if (uvs) {
                    newGeometry.uvs = new Float32Array(uvs);
                    // Babylon 6 double-normalizes Draco UVs stored as normalized unsigned shorts.
                    let maxUv = 0;
                    for (const uv of uvs) {
                        maxUv = Math.max(maxUv, Math.abs(uv));
                    }
                    if (normalizedDracoUvs && maxUv > 0 && maxUv <= 1.01 / 65535) {
                        for (let i = 0; i < newGeometry.uvs.length; i++) {
                            newGeometry.uvs[i] *= 65535;
                        }
                    }
                }

                const meshNode = manager.create(Token.CustomMeshNode);
                meshNode.name = mesh.name;
                // Preserve the loader's face orientation together with its original triangle winding.
                meshNode.flipSurfaceOrientation = mesh.overrideMaterialSideOrientation === Mesh.BACKSIDE;
                meshNode.position = KbVector.FromVec3(mesh.position);
                if (mesh.rotationQuaternion) {
                    const q = KbQuaternion.FromQuat(mesh.rotationQuaternion);
                    meshNode.rotationAngle = q.getAngle();
                    meshNode.rotationAxis = q.getAxis();
                } else {
                    meshNode.eulerRotation = KbVector.FromVec3(mesh.rotation);
                }
                meshNode.scaling = mesh.scaling.x;
                meshNode.visible = true;
                meshNode._geometry = newGeometry;

                if (mesh.material) {
                    const materialNode = materialMapping.get(mesh.material);
                    if (materialNode) {
                        meshNode.materialId = materialNode.id;
                    }
                }

                meshNodes.push(meshNode);
            }

            // For Testing
            // container.addAllToScene();
        }

        return { meshNodes, materialNodes, textureNodes, imageDataByUrl };

        async function loadGlb(file: File) {
            const fileData = await new Promise<ArrayBuffer>((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = () => {
                    if (!(reader.result instanceof ArrayBuffer)) {
                        reject(new Error('Failed to read file as ArrayBuffer'));
                        return;
                    }
                    resolve(reader.result);
                };

                reader.onerror = () => {
                    reject(reader.error);
                };

                reader.readAsArrayBuffer(file);
            });

            const blobUrl = URL.createObjectURL(new Blob([fileData]));
            const container = await SceneLoader.LoadAssetContainerAsync(blobUrl, '', viewer.scene, null, '.glb');

            return { container, normalizedDracoUvs: hasNormalizedDracoUvs(fileData) };
        }
    }
}
